import type { StockPhotoId } from "@/data/stockPhotos";

export interface PricingTier {
  badge: string;
  name: string;
  priceRange: string;
  paymentNote: string;
  includes: string[];
  indicatedFor: string;
  cta: string;
  highlight: boolean;
}

export interface AutomacaoPillar {
  slug: "crm" | "atendimento" | "erp";
  systemName: string;
  systemLicense: string;
  tag: string;
  short: string;
  title: string;
  promise: string;
  painPoint: string;
  aiAngle: string;
  fullDescription: string;
  features: string[];
  logo: string;
  screenshot: string;
  screenshotAlt: string;
  /** Foto de banco livre ao lado do "O que está incluso" (ver stockPhotos.ts). */
  photo?: StockPhotoId;
  videoId: string | null;
  videoLabel: string;
  from: string;
}

/**
 * Preço de entrada da automação — fonte única para home, /servicos e pilares.
 * O piso do Tier 1 é montado a partir dela.
 */
export const AUTOMACAO_FROM = "R$ 1.500";

export const automacaoTiers: PricingTier[] = [
  {
    badge: "Tier 1",
    name: "Implantação Essencial",
    priceRange: `${AUTOMACAO_FROM} – 3.500`,
    paymentNote: "Pagamento único",
    includes: [
      "Instalação e configuração completa",
      "Migração de dados e histórico básico",
      "Treinamento da equipe (1 sessão)",
    ],
    indicatedFor: "Pra quem só quer sair da planilha e do WhatsApp pessoal.",
    cta: "Quero começar simples",
    highlight: false,
  },
  {
    badge: "Mais escolhido",
    name: "Implantação + Automação com IA",
    priceRange: "R$ 4.000 – 9.000",
    paymentNote: "Único ou parcelado + hospedagem",
    includes: [
      "Tudo do Tier 1",
      "Workflows de automação via n8n",
      "Integração com WhatsApp",
      "1 agente de IA configurado pro seu processo",
    ],
    indicatedFor: "Pra quem quer eliminar trabalho manual repetitivo de vez.",
    cta: "Quero automatizar",
    highlight: true,
  },
  {
    badge: "Tier 3",
    name: "Operação Contínua",
    priceRange: "R$ 500 – 1.500/mês",
    paymentNote: "Recorrente",
    includes: [
      "Tudo do Tier 2",
      "Hospedagem e manutenção da instância",
      "Suporte mensal com SLA de resposta",
      "Novas automações periódicas",
    ],
    indicatedFor: "Pra quem quer parceiro contínuo, não projeto pontual.",
    cta: "Quero parceria contínua",
    highlight: false,
  },
];

export const automacaoSteps = [
  {
    n: "1",
    title: "Diagnóstico gratuito",
    desc: "30 minutos olhando seu processo real. Saio de lá com o que vale automatizar primeiro.",
    when: "Dia 1",
  },
  {
    n: "2",
    title: "Proposta fechada",
    desc: "Escopo, prazo e valor por escrito. Sem surpresa no meio do projeto.",
    when: "Até 3 dias",
  },
  {
    n: "3",
    title: "Implantação",
    desc: "Instalo, configuro pro seu funil e conecto as automações com IA.",
    when: "2 a 4 semanas",
  },
  {
    n: "4",
    title: "Treinamento e ajuste",
    desc: "Treino a equipe e acompanho os primeiros ciclos ajustando regras.",
    when: "Dia 30",
  },
];

export const automacaoFaq = [
  {
    q: "Preciso trocar o sistema que já uso?",
    a: "Não. Na maioria dos casos eu conecto o que já existe. A troca só entra em cena se o sistema atual for o próprio gargalo — e aí eu digo isso no diagnóstico, não depois de fechado.",
  },
  {
    q: "Quanto tempo até estar rodando?",
    a: "A implantação típica leva 30 dias: até 3 dias para a proposta, 2 a 4 semanas de configuração e automação, e o treinamento no fim.",
  },
  {
    q: "E se minha equipe não souber usar?",
    a: "Treinamento presencial ou remoto está incluso em todos os tiers, e eu acompanho os primeiros ciclos ajustando o que confundir a equipe.",
  },
  {
    q: "Fico preso a você depois?",
    a: "Não. O sistema é open source e roda na sua infraestrutura. Se um dia quiser levar para outro time, leva — inclusive a documentação.",
  },
  {
    q: "Por que a faixa de preço é ampla?",
    a: "Porque o esforço muda muito com o número de fluxos e integrações. A faixa fecha em um número exato depois do diagnóstico gratuito.",
  },
];

export const automacaoPillars: AutomacaoPillar[] = [
  {
    slug: "crm",
    systemName: "Twenty",
    systemLicense:
      "AGPL-3.0. Mantemos uma instância por cliente, sem fork multi-tenant — se o código for modificado e oferecido como SaaS, as modificações precisam ser publicadas.",
    tag: "CRM Inteligente",
    short: "CRM e follow-up",
    title: "CRM que qualifica lead, dispara follow-up e avisa a equipe sozinho",
    promise: "Nenhum lead esfria porque ninguém lembrou de responder.",
    painPoint:
      "Seu time de vendas perde lead porque ninguém responde a tempo — e você só descobre quando o cliente já fechou com outro.",
    aiAngle:
      "A IA lê o lead assim que ele entra, qualifica pelo histórico e dispara o primeiro contato antes que o time perceba que chegou. Follow-up automático quando algo fica parado tempo demais.",
    fullDescription:
      "Implantamos o Twenty — CRM open source moderno — configurado para o seu funil de vendas real, com automação por IA nos pontos onde lead esfria: qualificação automática, follow-up disparado sozinho e aviso pra equipe no momento certo.",
    features: [
      "Pipeline de vendas organizado por estágio, sem planilha paralela",
      "IA qualifica lead novo e sugere prioridade de atendimento",
      "Follow-up automático quando um lead fica parado tempo demais",
      "Aviso automático pra equipe certa no momento certo",
    ],
    logo: "/images/automacao/twenty-icon.svg",
    screenshot: "/images/automacao/twenty-screenshot.png",
    screenshotAlt: "Tela real do Twenty CRM mostrando tabela de empresas",
    photo: "conversa-vendas",
    videoId: null,
    videoLabel: "Demonstração por screenshot real do produto",
    from: AUTOMACAO_FROM,
  },
  {
    slug: "atendimento",
    systemName: "Chatwoot",
    systemLicense:
      "MIT no núcleo — permissivo, pode modificar e hospedar multi-tenant. Só a pasta enterprise/ tem licença separada; evitamos depender dela.",
    tag: "Atendimento com IA",
    short: "atendimento e triagem",
    title: "Uma caixa só de conversas que a IA tria e responde sozinha",
    promise: "WhatsApp, Instagram, site e e-mail num lugar só — com histórico.",
    painPoint:
      "Atendimento vira caos de WhatsApp pessoal: sem histórico, sem SLA, e cliente esperando resposta que ninguém viu.",
    aiAngle:
      "A IA responde as perguntas repetitivas na hora, 24 horas por dia, e só escala pra uma pessoa quando o assunto realmente precisa de gente.",
    fullDescription:
      "Implantamos o Chatwoot — plataforma de atendimento open source — unificando WhatsApp, Instagram, site e e-mail numa caixa só, com IA respondendo o que é repetitivo e triando o que precisa de humano.",
    features: [
      "WhatsApp, Instagram, site e e-mail numa única caixa de entrada",
      "IA responde dúvida repetitiva 24 horas, sem depender de plantão",
      "Histórico completo por cliente, sem se perder no WhatsApp pessoal",
      "Escalonamento automático pra equipe quando o assunto exige humano",
    ],
    logo: "/images/automacao/chatwoot-icon.svg",
    screenshot: "/images/automacao/chatwoot-screenshot.png",
    screenshotAlt: "Tela real do Chatwoot mostrando caixa de conversas com cliente",
    photo: "pedido-celular",
    videoId: "E4xUHyAAktY",
    videoLabel: "Vídeo oficial: Chatwoot — Product Walkthrough",
    from: AUTOMACAO_FROM,
  },
  {
    slug: "erp",
    systemName: "Odoo",
    systemLicense:
      "Odoo Community é LGPL-3.0 — permissivo o bastante pra customizar sem exigir publicação de código. A edição Enterprise tem licença proprietária à parte.",
    tag: "Gestão Integrada",
    short: "financeiro e estoque",
    title: "Financeiro, estoque e vendas conectados, com alerta antes do problema",
    promise: "Visão real do negócio sem depender de fechamento manual.",
    painPoint:
      "Financeiro e estoque em planilha, sem visão real do negócio — o problema aparece só depois que já custou caro.",
    aiAngle:
      "A IA cruza estoque, vendas e financeiro em tempo real e avisa antes do problema acontecer: ruptura de estoque, conta a vencer, oportunidade parada.",
    fullDescription:
      "Implantamos o Odoo Community — ERP open source — conectando financeiro, estoque e vendas num só lugar, com alertas automáticos nos pontos onde a planilha esconde risco.",
    features: [
      "Financeiro, estoque e vendas no mesmo sistema, sem planilha paralela",
      "Alerta automático de ruptura de estoque antes que aconteça",
      "Visão real de fluxo de caixa, sem depender de fechamento manual",
      "Relatórios que mostram o que está funcionando e o que está travando",
    ],
    logo: "/images/automacao/odoo-icon.svg",
    screenshot: "/images/automacao/odoo-screenshot.png",
    screenshotAlt: "Tela real do Odoo mostrando kanban de oportunidades",
    videoId: "nbso3NVz3p8",
    videoLabel: "Vídeo oficial: What is Odoo in two minutes",
    from: AUTOMACAO_FROM,
  },
];

// TEMP: pilar ERP (Odoo) oculto do site público. Reverter = `true`.
// Passo-a-passo completo em docs/specs/temporary-changes.md.
export const ERP_PILLAR_ENABLED = false;

export const visiblePillars = automacaoPillars.filter(
  (pillar) => pillar.slug !== "erp" || ERP_PILLAR_ENABLED,
);

export const getPillarBySlug = (slug: string) =>
  visiblePillars.find((pillar) => pillar.slug === slug);
